// The client's sign-in seam (crypto-launch-prompt lanes A and D). Free play never needs it; money
// play does (the stake buttons ask for sign-in first). The rest of the client (menu, money UI,
// socket HELLO, money/account HTTP calls) only ever sees this interface:
//
//   const auth = await loadAuth(info.auth, deps);   // lazy: Privy's code loads only here
//   auth.user.subscribe(...)                        // null = signed out
//   await auth.signIn()                             // opens the provider's modal
//   const token = await auth.getAccessToken()       // HELLO authToken + `Authorization: Bearer`
//
// Who sends the token:
// - GameSocket HELLO carries `authToken` for MONEY rooms only (the Connector's money target);
//   free rooms connect anonymously, so a stale token can never cost a free game (ERROR 17).
// - net/money-api.ts and net/account-api.ts send `Authorization: Bearer` on every
//   /api/money/* (except find) and /api/account/* call; no call carries an account id any more.
// - On ERROR 17 AUTH_INVALID (close 4017) the client calls `getAccessToken()` again (Privy
//   refreshes) and reconnects once; a second failure signs out.
//
// Implementations:
// - auth/fake.ts (`provider: 'fake'`, dev): a local account id kept in localStorage, tokens minted
//   with @ballio/shared encodeFakeAuthToken. The server accepts them only with auth.provider
//   "fake" (never in production).
// - auth/privy-island.ts + auth/privy-core.ts (`provider: 'privy'`): a lazily imported React
//   island around @privy-io/react-auth (email, Google, X or an external Solana wallet; an embedded
//   Solana wallet is created on login for users without one), mounted into its own <div> under <body>; privy-core.ts is the React-free logic (unit-tested
//   against a mocked SDK port). Why React: Privy's supported web SDK is the React one (modal UI,
//   embedded-wallet creation and signing, `useFundWallet` for lane D); the framework-free core
//   SDK has no UI and no supported embedded-wallet flow. The island keeps React and Privy out of
//   the game bundle: they load only when the player presses "Sign in" (or, on a later visit, when
//   an earlier sign-in is being restored), never on the free-play path.
//
// The app talks to the module through auth/session.ts (AccountSession: lazy load, restore on
// boot, token retry, username), never directly.

import type { WalletKind } from '@ballio/shared';
import type { ReadonlySignal } from '../ui/dom.js';

/** Why the account has no usable payment wallet (display only; the server decides money). */
export type WalletProblem =
  /** The server stores a payment wallet the player unlinked in Privy: money is paused. */
  | 'unlinked'
  /** The wallet the rule picks is already another ball.io account's (409 walletMismatch). */
  | 'taken'
  /** The server could not store one (noWallet, no identity token, a failed call). */
  | 'setupFailed';

/** The signed-in player, as the menu and money UI show it. */
export interface AuthUser {
  /** The Privy user id (DID); the server's account id. */
  readonly accountId: string;
  /** null until the player picked one (asked before the first money game). */
  readonly username: string | null;
  /**
   * The payment wallet's address (base58; @ballio/shared `selectPaymentWallet`: the embedded
   * Solana wallet, else the external one signed in with). Once the server answered GET
   * /api/account/me this is ONLY the wallet the server stores (null when it has none, whatever
   * Privy holds); before that, the Privy user's pick, for display while the read is in flight.
   * Money code (buy-in sender, Add Funds) must use it only when `walletProblem` is null.
   */
  readonly wallet: string | null;
  /** How `wallet` is held (display only); null / absent when unknown. */
  readonly walletKind?: WalletKind | null;
  /** Why there is no usable payment wallet; null / absent when there is none to report. */
  readonly walletProblem?: WalletProblem | null;
  /** For "signed in as …": the email, Google address or X `@handle`; null when unknown. */
  readonly email: string | null;
  /** Wall ms a ban ends (`Infinity` = permanent), null / absent when not banned (GET /me). */
  readonly bannedUntil?: number | null;
}

export type AuthStatus =
  /** The provider is still starting (restoring an earlier session). */
  'loading' | 'signedOut' | 'signedIn';

export interface AuthModule {
  readonly provider: 'fake' | 'privy';
  readonly status: ReadonlySignal<AuthStatus>;
  /** null while signed out or loading. */
  readonly user: ReadonlySignal<AuthUser | null>;
  /**
   * Opens the provider's sign-in modal. Resolves true once signed in (the account was fetched
   * from GET /api/account/me and the wallet linked), false when the player closed the modal.
   */
  signIn(): Promise<boolean>;
  signOut(): Promise<void>;
  /**
   * A currently valid access token (refreshed when close to expiry), or null when signed out.
   * `fresh`: do not reuse the cached one (the server just refused it: ERROR 17 / HTTP 401).
   */
  getAccessToken(opts?: { readonly fresh?: boolean }): Promise<string | null>;
  /** A Privy identity token (POST /api/account/wallet), or null when signed out. */
  getIdentityToken(): Promise<string | null>;
  /** Re-reads GET /api/account/me into `user` (after setting the username). */
  refreshAccount(): Promise<void>;
}

/** `GET /api/info` → `auth` (absent on an older server = no sign-in there). */
export interface AuthInfo {
  readonly provider: 'fake' | 'privy';
  readonly privyAppId: string;
}

/** Validates `/api/info.auth`; null when absent or malformed (sign-in unavailable). */
export function parseAuthInfo(raw: unknown): AuthInfo | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const appId = typeof o['privyAppId'] === 'string' ? o['privyAppId'] : '';
  if (o['provider'] === 'fake') return { provider: 'fake', privyAppId: '' };
  if (o['provider'] === 'privy' && appId !== '') return { provider: 'privy', privyAppId: appId };
  return null;
}
